import React, { useEffect, useState } from 'react';
import { useFreeTranslation } from './hooks/useFreeTranslation';
import i18n from './i18n';
import axios from 'axios';
import { API_URL } from './constants';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Pagination
} from '@mui/material';
import { 
  Calendar, 
  TrendingUp, 
  Image as ImageIcon,
  Link as LinkIcon,
  Storefront,
  Clock,
  Eye,
  EyeSlash,
  Plus as Add,
  Minus as Remove,
  ChartBar,
  CaretDown,
  Globe
} from '@phosphor-icons/react';
import BannerScreenshotsPanel from './BannerScreenshotsPanel';
import { useAuth } from './AuthContext';
import { useUserModules } from './UserModulesContext';

const BANNERS_PER_PAGE = 100;
const t = i18n.getFixedT('en');

// Keep the calendar day from API timestamps. `new Date(...).toISOString()` shifts
// midnight datetimes back a day in timezones ahead of UTC, which empties the histogram.
const toDateKey = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const compact = value.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const bannerSeenRange = (banner) => ({
  firstSeen: toDateKey(banner.first_seen || banner.appearance_date || banner.date),
  lastSeen: toDateKey(banner.last_seen || banner.disappearance_date),
});

const BannerEvolutionView = () => {
  const { isAdmin } = useAuth();
  const { isModuleEnabled } = useUserModules();
  const showAiInsights = isAdmin && isModuleEnabled('experimental-ai-banner-insights');
  const { translateText, isTranslating, translationCount } = useFreeTranslation();
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState([0, 100]); // Percentage range for slider
  const [availableDates, setAvailableDates] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [appearedBanners, setAppearedBanners] = useState([]);
  const [disappearedBanners, setDisappearedBanners] = useState([]);
  const [lifecycleBanners, setLifecycleBanners] = useState([]);
  const [initialBannersCount, setInitialBannersCount] = useState(0);
  const [remainingBannersCount, setRemainingBannersCount] = useState(0);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedBannerTypes, setSelectedBannerTypes] = useState([]);
  const [scrollToBanners, setScrollToBanners] = useState(false);
  const [stackedView, setStackedView] = useState(false);
  const [textOverlay, setTextOverlay] = useState(false);
  const [translatedTexts, setTranslatedTexts] = useState({});
  const [translationLoading, setTranslationLoading] = useState({});
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInsightsDialogOpen, setAiInsightsDialogOpen] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState(0);
  const [bannerPage, setBannerPage] = useState(1);

  // Function to extract dates from banner data
  const extractDates = (banners) => {
    const dates = new Set();
    
    banners.forEach(banner => {
      // Try multiple possible date field names - prioritize 'date' field from evolution data
      const dateFields = ['date', 'scraped_date', 'created_at', 'timestamp', 'scraped_at', 'date_scraped', 'date_created'];
      
      dateFields.forEach(field => {
        const dateKey = toDateKey(banner[field]);
        if (dateKey) dates.add(dateKey);
      });
    });
    
    return Array.from(dates).sort();
  };

  // Function to get date from percentage
  const getDateFromPercentage = (percentage) => {
    if (availableDates.length === 0) return null;
    
    // Ensure percentage is between 0 and 100
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    
    // Calculate index based on percentage
    const index = Math.floor((clampedPercentage / 100) * (availableDates.length - 1));
    const clampedIndex = Math.max(0, Math.min(index, availableDates.length - 1));
    
    const selectedDate = availableDates[clampedIndex];
    console.log(`Percentage: ${clampedPercentage}%, Index: ${clampedIndex}, Date: ${selectedDate}`);
    
    return selectedDate;
  };

  // Function to get percentage from date (for reverse calculation)
  const getPercentageFromDate = (date) => {
    if (availableDates.length === 0) return 0;
    
    const index = availableDates.indexOf(date);
    if (index === -1) return 0;
    
    const percentage = (index / (availableDates.length - 1)) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  // Function to determine banner status based on date range
  const getBannerStatus = (banner, startDate, endDate) => {
    const { firstSeen, lastSeen } = bannerSeenRange(banner);
    
    // New: appeared during the date range
    if (firstSeen >= startDate && firstSeen <= endDate) {
      return 'new';
    }
    
    // Removed: disappeared during the date range (only if disappearance_date exists)
    if (lastSeen && lastSeen >= startDate && lastSeen <= endDate && firstSeen < startDate) {
      return 'removed';
    }
    
    // Active: was active during the date range
    if (firstSeen <= endDate && (!lastSeen || lastSeen >= startDate)) {
      return 'active';
    }
    
    return 'active'; // Default fallback
  };

  // Function to apply all filters
  const applyFilters = (banners, startDate, endDate) => {
    return banners.filter(banner => {
      // Country filter
      if (selectedCountries.length > 0 && !selectedCountries.includes(banner.country)) {
        return false;
      }
      
      // Retailer filter
      if (selectedRetailers.length > 0 && !selectedRetailers.includes(banner.retailer)) {
        return false;
      }
      
      // Status filter
      if (selectedStatuses.length > 0) {
        const bannerStatus = getBannerStatus(banner, startDate, endDate);
        if (!selectedStatuses.includes(bannerStatus)) {
          return false;
        }
      }
      
      // Banner type filter
      if (selectedBannerTypes.length > 0) {
        const bannerType = banner.category || 'Unknown';
        if (!selectedBannerTypes.includes(bannerType)) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Function to create histogram data
  const createHistogramData = () => {
    if (!bannerData || availableDates.length === 0) return [];
    
    const startDate = getDateFromPercentage(dateRange[0]);
    const endDate = getDateFromPercentage(dateRange[1]);
    
    // Apply filters to the data for histogram
    const filteredBannerData = bannerData.filter(banner => {
      // Country filter
      if (selectedCountries.length > 0 && !selectedCountries.includes(banner.country)) {
        return false;
      }
      
      // Retailer filter
      if (selectedRetailers.length > 0 && !selectedRetailers.includes(banner.retailer)) {
        return false;
      }
      
      // Banner type filter
      if (selectedBannerTypes.length > 0) {
        const bannerType = banner.category || 'Unknown';
        if (!selectedBannerTypes.includes(bannerType)) {
          return false;
        }
      }
      
      return true;
    });
    
      const histogram = availableDates.map((date, index) => {
        // For new/removed counts, compare with previous day
        let newCount = 0;
        let removedCount = 0;
        let activeCount = 0;
        let activeBanners = [];
        
        if (index > 0) {
          const previousDate = availableDates[index - 1];
          
          // Get banners active on previous date
          const previousActiveBanners = filteredBannerData.filter(banner => {
            const { firstSeen, lastSeen } = bannerSeenRange(banner);
            return firstSeen && firstSeen <= previousDate && (!lastSeen || lastSeen >= previousDate);
          });
          
          // Get banners active on current date
          const currentActiveBanners = filteredBannerData.filter(banner => {
            const { firstSeen, lastSeen } = bannerSeenRange(banner);
            return firstSeen && firstSeen <= date && (!lastSeen || lastSeen >= date);
          });
          
          // Find banners that appeared (in current but not in previous)
          const previousBannerKeys = new Set(previousActiveBanners.map(b => b.banner_key || `${b.retailer}_${b.country}_${b.text}`));
          const currentBannerKeys = new Set(currentActiveBanners.map(b => b.banner_key || `${b.retailer}_${b.country}_${b.text}`));
          
          newCount = currentActiveBanners.filter(banner => {
            const bannerKey = banner.banner_key || `${banner.retailer}_${banner.country}_${banner.text}`;
            return !previousBannerKeys.has(bannerKey);
          }).length;
          
          // Find banners that disappeared (in previous but not in current)
          removedCount = previousActiveBanners.filter(banner => {
            const bannerKey = banner.banner_key || `${banner.retailer}_${banner.country}_${banner.text}`;
            return !currentBannerKeys.has(bannerKey);
          }).length;
          
          // Active count = banners that were active on previous day and are still active today
          // This shows the persistent banners (not including new or removed)
          const persistentBanners = previousActiveBanners.filter(banner => {
            const bannerKey = banner.banner_key || `${banner.retailer}_${banner.country}_${banner.text}`;
            return currentBannerKeys.has(bannerKey);
          });
          activeCount = persistentBanners.length;
          activeBanners = persistentBanners;
        } else {
          // For the first date, all banners are considered "active" (not "new")
          // This prevents showing all banners as "new" on the first date
          newCount = 0;
          removedCount = 0;
          activeBanners = filteredBannerData.filter(banner => {
            const { firstSeen, lastSeen } = bannerSeenRange(banner);
            return firstSeen && firstSeen <= date && (!lastSeen || lastSeen >= date);
          });
          activeCount = activeBanners.length;
        }
        
        // Count active banners by brand for stacked view
        const iqosCount = activeBanners.filter(banner => 
          (banner.brand || '').toLowerCase().includes('iqos') || 
          (banner.retailer || '').toLowerCase().includes('iqos')
        ).length;
        
        const ploomCount = activeBanners.filter(banner => 
          (banner.brand || '').toLowerCase().includes('ploom') || 
          (banner.retailer || '').toLowerCase().includes('ploom')
        ).length;
        
        const gloCount = activeBanners.filter(banner => 
          (banner.brand || '').toLowerCase().includes('glo') || 
          (banner.retailer || '').toLowerCase().includes('glo')
        ).length;
      
      return {
        date,
        activeCount,
        newCount,
        removedCount,
        netChange: newCount - removedCount,
        // Brand counts for active banners only
        iqosCount,
        ploomCount,
        gloCount
      };
    });
    
    return histogram;
  };

  // Function to filter banners by date range
  const filterBannersByDateRange = (banners, startDate, endDate) => {
    return banners.filter(banner => {
      const dateFields = ['date', 'scraped_date', 'created_at', 'timestamp', 'scraped_at', 'date_scraped', 'date_created'];
      
      for (const field of dateFields) {
        if (banner[field]) {
          let bannerDate;
          // Handle different date formats
          if (typeof banner[field] === 'string') {
            bannerDate = new Date(banner[field]);
          } else if (banner[field] instanceof Date) {
            bannerDate = banner[field];
          } else {
            bannerDate = new Date(banner[field]);
          }
          
          if (!isNaN(bannerDate.getTime())) {
            const bannerDateStr = bannerDate.toISOString().split('T')[0];
            return bannerDateStr >= startDate && bannerDateStr <= endDate;
          }
        }
      }
      return false;
    });
  };

  // Function to create banner lifecycle data (deduplicated banners with appearance/disappearance dates)
  const createBannerLifecycle = (allBanners) => {
    const bannerMap = new Map();
    
    // Create a function to generate a unique key for banner comparison (excluding date fields)
    const getBannerKey = (banner) => {
      // Normalize function to clean and standardize field values
      const normalize = (value) => {
        if (!value) return '';
        return String(value)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .replace(/[^\w\s]/g, '') // Remove special characters except word chars and spaces
          .substring(0, 100); // Limit length to avoid extremely long keys
      };
      
      // Use all identifying fields except date-related ones
      const keyFields = [
        normalize(banner.retailer),
        normalize(banner.country),
        normalize(banner.url),
        normalize(banner.text),
        normalize(banner.description),
        normalize(banner.category),
        normalize(banner.cta_text),
        normalize(banner.image),
        normalize(banner.image_mobile),
        normalize(banner.source_page)
      ];
      
      // Create a normalized key by joining non-empty fields
      const normalizedKey = keyFields
        .filter(field => field.length > 0)
        .join('|');
      
      // If no meaningful fields, use a fallback based on available data
      if (normalizedKey.length === 0) {
        return `empty_${banner.retailer || 'unknown'}_${banner.country || 'unknown'}`;
      }
      
      return normalizedKey;
    };
    
    console.log('Processing banners for lifecycle:', allBanners.length);
    
    // Process all banners to track their lifecycle
    allBanners.forEach((banner, index) => {
      const key = getBannerKey(banner);
      const bannerDate = banner.date || banner.scraped_date || banner.created_at;
      
      // Debug logging for first few banners
      if (index < 5) {
        console.log(`Banner ${index}:`, {
          key: key.substring(0, 100) + '...',
          date: bannerDate,
          retailer: banner.retailer,
          country: banner.country,
          text: banner.text?.substring(0, 50)
        });
      }
      
      if (!bannerMap.has(key)) {
        // First time seeing this banner
        bannerMap.set(key, {
          ...banner,
          bannerKey: key,
          firstSeen: bannerDate,
          lastSeen: bannerDate,
          appearanceDate: bannerDate,
          disappearanceDate: null,
          status: 'active',
          totalOccurrences: 1,
          allDates: [bannerDate]
        });
      } else {
        // Update existing banner
        const existingBanner = bannerMap.get(key);
        existingBanner.totalOccurrences += 1;
        existingBanner.allDates.push(bannerDate);
        
        // Update first and last seen dates
        if (bannerDate < existingBanner.firstSeen) {
          existingBanner.firstSeen = bannerDate;
          existingBanner.appearanceDate = bannerDate;
        }
        if (bannerDate > existingBanner.lastSeen) {
          existingBanner.lastSeen = bannerDate;
        }
      }
    });
    
    console.log('Banner deduplication results:', {
      originalBanners: allBanners.length,
      uniqueBanners: bannerMap.size,
      reduction: allBanners.length - bannerMap.size
    });
    
    // Convert to array and sort by appearance date
    return Array.from(bannerMap.values()).sort((a, b) => new Date(a.appearanceDate) - new Date(b.appearanceDate));
  };

  // Function to find appeared and disappeared banners within date range
  const findBannerChanges = (allBanners, startDate, endDate) => {
    const lifecycleBanners = createBannerLifecycle(allBanners);
    
    // Filter banners that appeared in the selected date range
    const appeared = lifecycleBanners.filter(banner => {
      const appearanceDate = banner.appearanceDate;
      return appearanceDate >= startDate && appearanceDate <= endDate;
    });
    
    // Filter banners that disappeared in the selected date range
    const disappeared = lifecycleBanners.filter(banner => {
      // A banner is considered "disappeared" if its last seen date is before the end date
      // and it was active before the start date
      const lastSeen = banner.lastSeen;
      const firstSeen = banner.firstSeen;
      return firstSeen < startDate && lastSeen >= startDate && lastSeen <= endDate;
    });
    
    return { appeared, disappeared, lifecycleBanners };
  };

  // Function to group banners by country and retailer
  const groupBannersByCountryAndRetailer = (banners) => {
    const countryGroups = {};
    
    banners.forEach(banner => {
      const country = banner.country || 'Unknown';
      const retailer = banner.retailer || 'Unknown';
      
      if (!countryGroups[country]) {
        countryGroups[country] = {
          banners: [],
          retailers: {}
        };
      }
      
      countryGroups[country].banners.push(banner);
      
      if (!countryGroups[country].retailers[retailer]) {
        countryGroups[country].retailers[retailer] = [];
      }
      countryGroups[country].retailers[retailer].push(banner);
    });
    
    return countryGroups;
  };

  // Function to get brand color
  const getBrandColor = (brand) => {
    const brandColors = {
      'IQOS': '#2e7d32',
      'Glo': '#4caf50',
      'Ploom': '#1976d2',
      'Vuse': '#ff9800',
      'Juul': '#9c27b0',
      'Logic': '#795548',
      'Blu': '#607d8b',
      'Default': '#666'
    };
    return brandColors[brand] || brandColors['Default'];
  };

  // Function to translate individual banner text
  const translateIndividualBanner = async (text) => {
    if (!text) return;
    
    const currentLang = i18n.language || 'en';
    
    // Set loading state
    setTranslationLoading(prev => ({ ...prev, [text]: true }));
    
    try {
      const translatedText = await translateText(text, currentLang, 'auto');
      
      // Update translated texts
      setTranslatedTexts(prev => ({ ...prev, [text]: translatedText }));
    } catch (error) {
      console.warn('Translation failed:', error);
      // Keep original text on error
      setTranslatedTexts(prev => ({ ...prev, [text]: text }));
    } finally {
      // Clear loading state
      setTranslationLoading(prev => {
        const newState = { ...prev };
        delete newState[text];
        return newState;
      });
    }
  };

  // Helper function to extract date from banner object
  const extractBannerDate = (banner) => {
    const dateFields = ['date', 'scraped_date', 'created_at', 'timestamp', 'scraped_at', 'date_scraped', 'date_created'];
    
    for (const field of dateFields) {
      if (banner[field]) {
        let date;
        if (typeof banner[field] === 'string') {
          date = new Date(banner[field]);
        } else if (banner[field] instanceof Date) {
          date = banner[field];
        }
        
        if (date && !isNaN(date.getTime())) {
          return date;
        }
      }
    }
    return null;
  };

  const analyzeBannerChanges = async () => {
    if (!bannerData || !availableDates.length) return;
    
    setAiLoading(true);
    try {
      // Convert percentage-based dateRange to actual array indices
      const startIndex = Math.floor((dateRange[0] / 100) * (availableDates.length - 1));
      const endIndex = Math.floor((dateRange[1] / 100) * (availableDates.length - 1));
      
      // Get the current date range (availableDates contains date strings in YYYY-MM-DD format)
      const startDateStr = availableDates[startIndex];
      const endDateStr = availableDates[endIndex];
      
      console.log('Date range analysis:', {
        dateRange,
        availableDatesLength: availableDates.length,
        startIndex,
        endIndex,
        startDateStr,
        endDateStr
      });
      
      // Validate date strings
      if (!startDateStr || !endDateStr) {
        throw new Error(`Invalid date range: start=${startDateStr}, end=${endDateStr}`);
      }
      
      // Convert date strings to Date objects for comparison
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      
      // Prepare banner data for analysis
      const analysisData = bannerData.filter(banner => {
        const bannerDate = extractBannerDate(banner);
        return bannerDate && bannerDate >= startDate && bannerDate <= endDate;
      });
      
      console.log('Sending AI analysis request:', {
        start_date: startDateStr,
        end_date: endDateStr,
        banner_data_count: analysisData.length
      });
      
      const response = await axios.post(`${API_URL}/jti/analyze-banner-changes`, {
        start_date: startDateStr, // Use the string directly since it's already in YYYY-MM-DD format
        end_date: endDateStr,     // Use the string directly since it's already in YYYY-MM-DD format
        banner_data: analysisData
      });
      
      if (response.data.success) {
        setAiInsights(response.data);
        setAiInsightsDialogOpen(true);
      }
    } catch (error) {
      console.error('AI analysis failed:', error);
      setError('Failed to generate AI insights. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Function to render banner preview card
  const renderBannerCard = (banner, index, type = 'normal') => {
    const hasImage = banner.image || banner.image_mobile;
    const imageUrl = banner.image || banner.image_mobile;
    
    // Determine banner status based on selected date range
    const startDate = getDateFromPercentage(dateRange[0]);
    const endDate = getDateFromPercentage(dateRange[1]);
    const bannerStatus = getBannerStatus(banner, startDate, endDate);
    
    // Determine card styling based on status and view mode
    let bgColor = '#f5f5f5';
    let borderColor = '1px solid #e0e0e0';
    let statusColor = '#666';
    let statusText = 'Active';
    
    // Apply brand color if stacked view is enabled
    if (stackedView) {
      const brandColor = getBrandColor(banner.retailer || 'Default');
      bgColor = `${brandColor}20`; // 20% opacity
      borderColor = `2px solid ${brandColor}`;
    } else {
      // Original status-based styling
      if (bannerStatus === 'new') {
        bgColor = '#e8f5e8';
        borderColor = '2px solid #4caf50';
        statusColor = '#4caf50';
        statusText = 'Added';
      } else if (bannerStatus === 'removed') {
        bgColor = '#ffeaea';
        borderColor = '2px solid #f44336';
        statusColor = '#f44336';
        statusText = 'Removed';
      } else {
        bgColor = '#f0f8ff';
        borderColor = '2px solid #1976d2';
        statusColor = '#1976d2';
        statusText = 'Active';
      }
    }
    
    return (
      <ImageListItem key={index} sx={{ cursor: 'pointer' }} onClick={() => {
        setSelectedBanner(banner);
        setBannerDialogOpen(true);
      }}>
        <Box
          sx={{
            width: '100%',
            height: type === 'lifecycle' ? 450 : 400,
            bgcolor: bgColor,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            overflow: 'hidden',
            border: borderColor
          }}
        >
          {/* Image section */}
          <Box sx={{ height: 280, position: 'relative' }}>
            {hasImage ? (
              <>
                <img
                  src={imageUrl}
                  alt={banner.text || banner.description || 'Banner'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                {/* Text overlay */}
                {textOverlay && (banner.text || banner.description) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      p: 1,
                      color: 'white'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                      }}
                    >
                      {translationLoading[banner.text || banner.description] ? 
                        '🔄 Translating...' : 
                        (translatedTexts[banner.text || banner.description] || banner.text || banner.description)
                      }
                    </Typography>
                  </Box>
                )}
                <Box 
                  display="none" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <ImageIcon size={48} color="#ccc" />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Image Failed to Load
                  </Typography>
                </Box>
              </>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center"
                sx={{ height: '100%' }}
              >
                <ImageIcon size={48} color="#ccc" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  No Image
                </Typography>
              </Box>
            )}
            
             {/* Status indicator */}
             <Box
               sx={{
                 position: 'absolute',
                 top: 8,
                 right: 8,
                 bgcolor: statusColor,
                 color: 'white',
                 borderRadius: 1,
                 px: 1,
                 py: 0.5
               }}
             >
               <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                 {statusText.toUpperCase()}
               </Typography>
             </Box>
          </Box>
          
          {/* Metadata section below image */}
          <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" noWrap sx={{ mb: 1, fontWeight: 'bold' }}>
                {translationLoading[banner.text || banner.description] ? 
                  '🔄 Translating...' : 
                  (translatedTexts[banner.text || banner.description] || banner.text || banner.description || 'Banner')
                }
              </Typography>
              
              <Box sx={{ mb: 1 }}>
                <Chip 
                  size="small" 
                  label={banner.category || 'Unknown'} 
                  color="primary" 
                  variant="outlined"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
                {banner.country && (
                  <Chip 
                    size="small" 
                    label={banner.country} 
                    color="secondary" 
                    variant="outlined"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                )}
                {banner.retailer && (
                  <Chip 
                    size="small" 
                    label={banner.retailer} 
                    color="success" 
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                )}
              </Box>
              
              {banner.cta_text && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <strong>CTA:</strong> {banner.cta_text}
                </Typography>
              )}
              
              {banner.url && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <strong>URL:</strong> {banner.url.length > 50 ? banner.url.substring(0, 50) + '...' : banner.url}
                </Typography>
              )}
              
              {/* Lifecycle information */}
              {type === 'lifecycle' && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block' }}>
                    📅 Banner Lifecycle
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>First Seen:</strong> {banner.appearanceDate || banner.firstSeen}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Last Seen:</strong> {banner.lastSeen}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>Occurrences:</strong> {banner.totalOccurrences || 1}
                  </Typography>
                  {banner.allDates && banner.allDates.length > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <strong>Active Days:</strong> {banner.allDates.length}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
             <Box display="flex" justifyContent="flex-end">
               <Button
                 size="small"
                 variant="contained"
                 startIcon={<Eye size={16} />}
                 onClick={(e) => {
                   e.stopPropagation();
                   setSelectedBanner(banner);
                   setBannerDialogOpen(true);
                 }}
               >
                 Show Detail
               </Button>
               <Button
                 size="small"
                 variant="outlined"
                 startIcon={translationLoading[banner.text || banner.description] ? 
                   <CircularProgress size={16} /> : 
                   <Globe size={16} />
                 }
                 disabled={translationLoading[banner.text || banner.description]}
                 onClick={(e) => {
                   e.stopPropagation();
                   translateIndividualBanner(banner.text || banner.description);
                 }}
                 sx={{ ml: 1 }}
               >
                 {translationLoading[banner.text || banner.description] ? 'Translating...' : 'Translate'}
               </Button>
             </Box>
          </Box>
        </Box>
      </ImageListItem>
    );
  };

  // Function to render grouped banners
  const renderGroupedBanners = (banners, type = 'normal') => {
    if (!banners || banners.length === 0) return null;
    
    const groupedBanners = groupBannersByCountryAndRetailer(banners);
    const countryEntries = Object.entries(groupedBanners);
    
    return (
      <Box>
        {countryEntries.map(([country, countryData]) => {
          const retailerEntries = Object.entries(countryData.retailers);
          
          return (
            <Card key={country} sx={{ mb: 2 }}>
              <CardContent>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center">
                    <Storefront size={20} style={{ marginRight: 8, color: '#1976d2' }} />
                    <Typography variant="h6">
                      {country} ({countryData.banners.length} banners)
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      size="small" 
                      label={`${retailerEntries.length} retailers`}
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
                
                <Box mt={2}>
                  {retailerEntries.map(([retailer, retailerBanners]) => {
                    return (
                      <Card key={`${country}-${retailer}`} sx={{ mb: 2, ml: 2 }} variant="outlined">
                        <CardContent>
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            justifyContent="space-between"
                          >
                            <Box display="flex" alignItems="center">
                              <Storefront size={16} style={{ marginRight: 8, color: '#2e7d32' }} />
                              <Typography variant="subtitle1">
                                {retailer} ({retailerBanners.length} banners)
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box mt={2}>
                            <ImageList 
                              cols={4} 
                              gap={16}
                              sx={{
                                width: '100%',
                                '& .MuiImageListItem-root': {
                                  minWidth: '200px !important'
                                }
                              }}
                            >
                              {retailerBanners.map((banner, index) => renderBannerCard(banner, index, type))}
                            </ImageList>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // Banner detail dialog
  const BannerDetailDialog = () => (
    <Dialog
      open={bannerDialogOpen}
      onClose={() => setBannerDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Storefront size={24} style={{ marginRight: 8 }} />
          Banner Details
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedBanner && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                {(selectedBanner.image || selectedBanner.image_mobile) ? (
                  <img
                    src={selectedBanner.image || selectedBanner.image_mobile}
                    alt={selectedBanner.text || selectedBanner.description || 'Banner'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <ImageIcon size={64} color="#ccc" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No Image Available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Banner Information
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {selectedBanner.category && (
                        <TableRow>
                          <TableCell><strong>Category:</strong></TableCell>
                          <TableCell>{selectedBanner.category}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.source_page && (
                        <TableRow>
                          <TableCell><strong>Source Page:</strong></TableCell>
                          <TableCell>{selectedBanner.source_page}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.country && (
                        <TableRow>
                          <TableCell><strong>Country:</strong></TableCell>
                          <TableCell>{selectedBanner.country}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.retailer && (
                        <TableRow>
                          <TableCell><strong>Retailer:</strong></TableCell>
                          <TableCell>{selectedBanner.retailer}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.text && (
                        <TableRow>
                          <TableCell><strong>Text:</strong></TableCell>
                          <TableCell>{selectedBanner.text}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.description && (
                        <TableRow>
                          <TableCell><strong>Description:</strong></TableCell>
                          <TableCell>{selectedBanner.description}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.cta_text && (
                        <TableRow>
                          <TableCell><strong>CTA Text:</strong></TableCell>
                          <TableCell>{selectedBanner.cta_text}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.url && (
                        <TableRow>
                          <TableCell><strong>URL:</strong></TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LinkIcon />}
                              onClick={() => window.open(selectedBanner.url, '_blank')}
                            >
                              Open Link
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBannerDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // AI Insights Dialog
  const AIInsightsDialog = () => (
    <Dialog
      open={aiInsightsDialogOpen}
      onClose={() => setAiInsightsDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChartBar size={24} />
          AI Banner Analysis Insights
        </Box>
      </DialogTitle>
      <DialogContent>
        {aiInsights && (
          <Box>
            {/* Period Information */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Analysis Period
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {aiInsights.period.start_date} to {aiInsights.period.end_date}
                {' '}({aiInsights.analysis.period_days} days)
              </Typography>
            </Box>

            {/* AI Insights */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                AI Insights
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                <Typography variant="body1">
                  {aiInsights.ai_insights}
                </Typography>
              </Paper>
            </Box>

            {/* Analysis Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Analysis Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="primary">
                        {aiInsights.analysis.total_banners}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Banners
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="secondary">
                        {aiInsights.analysis.summary.avg_banners_per_day}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg per Day
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Top Brands */}
            {aiInsights.analysis.top_brands.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Brands
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {aiInsights.analysis.top_brands.slice(0, 5).map(([brand, data]) => (
                    <Chip
                      key={brand}
                      label={`${brand}: ${data.count}`}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Top Retailers */}
            {aiInsights.analysis.top_retailers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Retailers
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {aiInsights.analysis.top_retailers.slice(0, 5).map(([retailer, data]) => (
                    <Chip
                      key={retailer}
                      label={`${retailer}: ${data.count}`}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Top Categories */}
            {aiInsights.analysis.top_categories.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {aiInsights.analysis.top_categories.slice(0, 5).map(([category, data]) => (
                    <Chip
                      key={category}
                      label={`${category}: ${data.count}`}
                      color="default"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAiInsightsDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_URL}/jti/banners-evolution-data`);
        
        console.log('Banner Evolution API response:', response.data);
        
        if (!response.data || !response.data.columns || !response.data.data) {
          console.error('Unexpected banner evolution API response structure:', response.data);
          setError('Invalid banner evolution data structure');
          return;
        }
        
        const { columns, data: rawData, evolution_stats } = response.data;
        console.log('Banner evolution columns:', columns);
        console.log('Banner evolution raw data count:', rawData.length);
        console.log('Evolution stats:', evolution_stats);
        
        // Convert raw data to objects with column names
        const processedBannerData = rawData.map(row => {
          const obj = {};
          columns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });

        setBannerData(processedBannerData);
        
        // Extract available dates - use evolution stats if available
        let dates = [];
        if (evolution_stats && evolution_stats.unique_dates > 0) {
          // Try to extract dates from the data
          dates = extractDates(processedBannerData);
          console.log('Extracted dates from data:', dates);
        } else {
          // Fallback to extracting from data
          dates = extractDates(processedBannerData);
        }
        
        // If no dates found, create a simple range
        if (dates.length === 0) {
          const today = new Date();
          const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          dates = [thirtyDaysAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]];
        }
        
        setAvailableDates(dates);
        
        // Initialize date range to show all data (0% to 100%)
        setDateRange([0, 100]);
        
        console.log('Available dates:', dates);
        console.log('Evolution statistics:', evolution_stats);
        
      } catch (err) {
        console.error('Error fetching banner evolution data:', err);
        setError('Failed to load banner evolution data');
      } finally {
        setLoading(false);
      }
    };

    fetchBannerData();
  }, []);

  // Update filtered banners when date range or filters change
  useEffect(() => {
    if (bannerData && availableDates.length > 0) {
      const startDate = getDateFromPercentage(dateRange[0]);
      const endDate = getDateFromPercentage(dateRange[1]);
      
      console.log('Filters changed:', { 
        startDate, 
        endDate, 
        selectedCountries, 
        selectedRetailers, 
        selectedStatuses 
      });
      
      if (startDate && endDate) {
        // Apply all filters to the data
        const filteredData = applyFilters(bannerData, startDate, endDate);
        
        // For appeared banners: banners that first appeared in the selected date range
        const appeared = filteredData.filter(banner => {
          const appearanceDate = toDateKey(banner.appearance_date || banner.first_seen);
          return appearanceDate && appearanceDate >= startDate && appearanceDate <= endDate;
        });
        
        // For disappeared banners: banners that disappeared in the selected date range
        const disappeared = filteredData.filter(banner => {
          const disappearanceDate = toDateKey(banner.disappearance_date || banner.last_seen);
          return disappearanceDate && disappearanceDate >= startDate && disappearanceDate <= endDate;
        });
        
         // Calculate initial banners (active banners on the first day of the selected date range)
         const initialBanners = filteredData.filter(banner => {
           const { firstSeen, lastSeen } = bannerSeenRange(banner);
           return firstSeen && firstSeen <= startDate && (!lastSeen || lastSeen >= startDate);
         });
         
         // Calculate remaining banners (active banners on the last day of the selected date range)
         const remainingBanners = filteredData.filter(banner => {
           const { firstSeen, lastSeen } = bannerSeenRange(banner);
           return firstSeen && firstSeen <= endDate && (!lastSeen || lastSeen >= endDate);
         });
        
         // For lifecycle view, show banners that were active during the selected date range
         const lifecycleBanners = filteredData.filter(banner => {
           const { firstSeen, lastSeen } = bannerSeenRange(banner);
           return firstSeen && firstSeen <= endDate && (!lastSeen || lastSeen >= startDate);
         });
        
        setFilteredBanners(filteredData);
        setAppearedBanners(appeared);
        setDisappearedBanners(disappeared);
        setLifecycleBanners(lifecycleBanners);
        setBannerPage(1);
        
         // Store calculated values for statistics
         setInitialBannersCount(initialBanners.length);
         setRemainingBannersCount(remainingBanners.length);
        
         console.log('Filters applied:', {
           startDate,
           endDate,
           totalBanners: bannerData.length,
           filteredBanners: filteredData.length,
           initialBanners: initialBanners.length,
           appearedBanners: appeared.length,
           disappearedBanners: disappeared.length,
           remainingBanners: remainingBanners.length
         });
      }
    }
  }, [bannerData, availableDates, dateRange, selectedCountries, selectedRetailers, selectedStatuses, selectedBannerTypes]);


  // Scroll effect for banner cards
  useEffect(() => {
    if (scrollToBanners) {
      const timer = setTimeout(() => {
        const bannerSection = document.getElementById('banner-cards-section');
        if (bannerSection) {
          bannerSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
        setScrollToBanners(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToBanners]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const startDate = getDateFromPercentage(dateRange[0]);
  const endDate = getDateFromPercentage(dateRange[1]);
  const histogramData = createHistogramData();
  const histogramMaxCount = histogramData.reduce(
    (max, item) => Math.max(max, item.activeCount, item.newCount, item.removedCount),
    0
  );

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('bannerEvolution.title')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {t('bannerEvolution.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={activeViewTab}
        onChange={(_, value) => setActiveViewTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('bannerEvolution.tabs.evolution')} />
        <Tab label={t('bannerEvolution.tabs.screenshots')} />
      </Tabs>

      {activeViewTab === 1 && <BannerScreenshotsPanel />}

      {activeViewTab === 0 && (
      <>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{t('bannerEvolution.note')}</strong>
        </Typography>
      </Alert>
      
      {bannerData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('bannerEvolution.datasetInfo', { 
              count: bannerData.length, 
              startDate: availableDates[0], 
              endDate: availableDates[availableDates.length - 1],
              dateCount: availableDates.length 
            })}</strong>
          </Typography>
        </Alert>
      )}
      
       <Divider sx={{ my: 3 }} />
       
       {/* Summary Statistics */}
       <Card sx={{ mb: 3 }}>
         <CardContent>
           <Typography variant="h6" gutterBottom>
             {t('bannerEvolution.evolutionSummary')}
           </Typography>
           <Grid container spacing={2}>
             <Grid item xs={12} md={3}>
               <Box display="flex" alignItems="center" p={2} bgcolor="#fff3e0" borderRadius={1}>
                 <Calendar size={24} style={{ marginRight: 8, color: '#ff9800' }} />
                 <Box>
                   <Typography variant="h4" color="warning.main">
                     {initialBannersCount}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     {t('bannerEvolution.initialBanners')}
                   </Typography>
                 </Box>
               </Box>
             </Grid>
             <Grid item xs={12} md={3}>
               <Box display="flex" alignItems="center" p={2} bgcolor="#e8f5e8" borderRadius={1}>
                 <Add size={24} style={{ marginRight: 8, color: '#4caf50' }} />
                 <Box>
                   <Typography variant="h4" color="success.main">
                     {appearedBanners.length}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     {t('bannerEvolution.addedBanners')}
                   </Typography>
                 </Box>
               </Box>
             </Grid>
             <Grid item xs={12} md={3}>
               <Box display="flex" alignItems="center" p={2} bgcolor="#ffebee" borderRadius={1}>
                 <Remove size={24} style={{ marginRight: 8, color: '#f44336' }} />
                 <Box>
                   <Typography variant="h4" color="error.main">
                     {disappearedBanners.length}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     {t('bannerEvolution.removedBanners')}
                   </Typography>
                 </Box>
               </Box>
             </Grid>
             <Grid item xs={12} md={3}>
               <Box display="flex" alignItems="center" p={2} bgcolor="#e3f2fd" borderRadius={1}>
                 <Storefront size={24} style={{ marginRight: 8, color: '#1976d2' }} />
                 <Box>
                   <Typography variant="h4" color="primary">
                     {remainingBannersCount}
                   </Typography>
                   <Typography variant="body2" color="text.secondary">
                     {t('bannerEvolution.remainingBanners')}
                   </Typography>
                 </Box>
               </Box>
             </Grid>
           </Grid>
         </CardContent>
       </Card>

       {/* Date Range Controls */}
       <Card sx={{ mb: 3 }}>
         <CardContent>
           <Typography variant="h6" gutterBottom>
             {t('bannerEvolution.dateRangeSelection')}
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             {t('bannerEvolution.dateRangeDescription')}
           </Typography>
           
           <Grid container spacing={3}>
             <Grid item xs={12} md={8}>
               {/* Histogram */}
               <Box sx={{ mb: 3 }}>
                 <Typography variant="subtitle2" gutterBottom>
                   {t('bannerEvolution.bannerEvolutionOverTime')}
                 </Typography>
                 <Box sx={{ display: 'flex', gap: 1 }}>
                   {/* Y-axis */}
                   <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 140, minWidth: 40 }}>
                     {(() => {
                       const ticks = [histogramMaxCount, Math.floor(histogramMaxCount * 0.75), Math.floor(histogramMaxCount * 0.5), Math.floor(histogramMaxCount * 0.25), 0];
                       return ticks.map((tick, index) => (
                         <Typography key={index} variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                           {tick}
                         </Typography>
                       ));
                     })()}
                   </Box>
                   
                   {/* Histogram bars. Many dates collapse to 0px if they only flex inside the card. */}
                   <Box sx={{ flex: 1, minWidth: 0, height: 140, overflowX: 'auto', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                   <Box sx={{
                     height: '100%',
                     minWidth: '100%',
                     width: histogramData.length > 60 ? `${histogramData.length * 6}px` : '100%',
                     display: 'flex',
                     alignItems: 'flex-end',
                     gap: '1px',
                     boxSizing: 'border-box',
                     px: 1,
                   }}>
                  {histogramData.map((item, index) => {
                    const isInRange = item.date >= startDate && item.date <= endDate;
                    
                    if (stackedView) {
                      // Brand-specific stacked view - only active banners by brand
                      const maxCount = histogramMaxCount;
                      const totalHeight = maxCount > 0 ? Math.max((item.activeCount / maxCount) * 100, 4) : 4;
                      
                      // Calculate brand proportions within active banners
                      const totalActiveBrands = item.iqosCount + item.ploomCount + item.gloCount;
                      const iqosHeight = totalActiveBrands > 0 ? (item.iqosCount / totalActiveBrands) * 100 : 0;
                      const ploomHeight = totalActiveBrands > 0 ? (item.ploomCount / totalActiveBrands) * 100 : 0;
                      const gloHeight = totalActiveBrands > 0 ? (item.gloCount / totalActiveBrands) * 100 : 0;
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            flex: '1 0 4px',
                            minWidth: 4,
                            height: `${totalHeight}%`,
                            position: 'relative',
                            cursor: 'pointer',
                            minHeight: 4,
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column-reverse'
                          }}
                          title={`${item.date}: Active ${item.activeCount} (IQOS ${item.iqosCount}, Ploom ${item.ploomCount}, Glo ${item.gloCount})`}
                        >
                          {/* Active banners - split by brand */}
                          {item.iqosCount > 0 && (
                            <Box
                              sx={{
                                height: `${iqosHeight}%`,
                                bgcolor: isInRange ? '#ff9800' : '#ffcc80', // Orange for IQOS
                                borderRadius: 0.5
                              }}
                            />
                          )}
                          
                          {item.ploomCount > 0 && (
                            <Box
                              sx={{
                                height: `${ploomHeight}%`,
                                bgcolor: isInRange ? '#1976d2' : '#90caf9', // Blue for Ploom
                                borderRadius: 0.5
                              }}
                            />
                          )}
                          
                          {item.gloCount > 0 && (
                            <Box
                              sx={{
                                height: `${gloHeight}%`,
                                bgcolor: isInRange ? '#9c27b0' : '#ce93d8', // Purple for Glo
                                borderRadius: 0.5
                              }}
                            />
                          )}
                        </Box>
                      );
                    } else {
                      // Original active/new/removed view
                      const maxCount = histogramMaxCount;
                      const activeHeight = maxCount > 0 ? (item.activeCount / maxCount) * 100 : 0;
                      const newHeight = maxCount > 0 ? (item.newCount / maxCount) * 100 : 0;
                      const removedHeight = maxCount > 0 ? (item.removedCount / maxCount) * 100 : 0;
                      const totalHeight = Math.max(activeHeight + newHeight + removedHeight, 4);
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            flex: '1 0 4px',
                            minWidth: 4,
                            height: `${totalHeight}%`,
                            position: 'relative',
                            cursor: 'pointer',
                            minHeight: 4,
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column-reverse'
                          }}
                          title={`${item.date}: ${item.activeCount} active, ${item.newCount} new, ${item.removedCount} removed`}
                        >
                          {/* Active banners */}
                          {item.activeCount > 0 && (
                            <Box
                              sx={{
                                height: `${activeHeight}%`,
                                bgcolor: isInRange ? '#1976d2' : '#e0e0e0',
                                borderRadius: 0.5
                              }}
                            />
                          )}
                          
                          {/* New banners */}
                          {item.newCount > 0 && (
                            <Box
                              sx={{
                                height: `${newHeight}%`,
                                bgcolor: isInRange ? '#4caf50' : '#a5d6a7',
                                borderRadius: 0.5
                              }}
                            />
                          )}
                          
                          {/* Removed banners */}
                          {item.removedCount > 0 && (
                            <Box
                              sx={{
                                height: `${removedHeight}%`,
                                bgcolor: isInRange ? '#f44336' : '#ef9a9a',
                                borderRadius: 0.5
                              }}
                            />
                          )}
                        </Box>
                      );
                    }
                  })}
                   </Box>
                   </Box>
                 </Box>
                 <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                   <Typography variant="caption" color="text.secondary">
                     {stackedView ? 
                       'Active banners by brand: Orange: IQOS | Blue: Ploom | Purple: Glo' : 
                       'Blue: Active banners | Green: New | Red: Removed'
                     }
                   </Typography>
                   <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                     {stackedView ? (
                       <>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#ff9800', borderRadius: 0.5 }} />
                           <Typography variant="caption">IQOS</Typography>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                           <Typography variant="caption">Ploom</Typography>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#9c27b0', borderRadius: 0.5 }} />
                           <Typography variant="caption">Glo</Typography>
                         </Box>
                       </>
                     ) : (
                       <>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: 0.5 }} />
                           <Typography variant="caption">Active</Typography>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                           <Typography variant="caption">New</Typography>
                         </Box>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <Box sx={{ width: 8, height: 8, bgcolor: '#f44336', borderRadius: 0.5 }} />
                           <Typography variant="caption">Removed</Typography>
                         </Box>
                       </>
                     )}
                     <Button
                       size="small"
                       variant="outlined"
                       startIcon={<CaretDown size={16} />}
                       onClick={() => setScrollToBanners(true)}
                       sx={{ ml: 2 }}
                     >
                       View Banners
                     </Button>
                   </Box>
                 </Box>
              </Box>
              
              {/* Compact Slider */}
              <Box sx={{ px: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Date Range: {startDate} to {endDate}
                </Typography>
                <Slider
                  value={dateRange}
                  onChange={(event, newValue) => {
                    console.log('Slider changed:', newValue);
                    setDateRange(newValue);
                  }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => {
                    const date = getDateFromPercentage(value);
                    return date ? new Date(date).toLocaleDateString() : 'No date';
                  }}
                  min={0}
                  max={100}
                  step={1}
                  sx={{ mt: 1, mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Drag sliders to select analysis period
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDateRange([0, 100])}
                    sx={{ ml: 2 }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Grid>
             <Grid item xs={12} md={4}>
               <Box display="flex" flexDirection="column" gap={2}>
                 <FormControl fullWidth size="small">
                  <InputLabel>{t('bannerEvolution.countries')}</InputLabel>
                  <Select
                    multiple
                    value={selectedCountries}
                    onChange={(e) => setSelectedCountries(e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? t('bannerEvolution.allCountries') : `${selected.length} selected`}
                  >
                    {bannerData && [...new Set(bannerData.map(b => b.country).filter(Boolean))].map(country => (
                      <MenuItem key={country} value={country}>{country}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth size="small">
                  <InputLabel>{t('bannerEvolution.retailers')}</InputLabel>
                  <Select
                    multiple
                    value={selectedRetailers}
                    onChange={(e) => setSelectedRetailers(e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? t('bannerEvolution.allRetailers') : `${selected.length} selected`}
                  >
                    {bannerData && [...new Set(bannerData.map(b => b.retailer).filter(Boolean))].map(retailer => (
                      <MenuItem key={retailer} value={retailer}>{retailer}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                 <FormControl fullWidth size="small">
                   <InputLabel>{t('bannerEvolution.status')}</InputLabel>
                   <Select
                     multiple
                     value={selectedStatuses}
                     onChange={(e) => setSelectedStatuses(e.target.value)}
                     renderValue={(selected) => selected.length === 0 ? t('bannerEvolution.allStatuses') : `${selected.length} selected`}
                   >
                     <MenuItem value="active">{t('bannerEvolution.active')}</MenuItem>
                     <MenuItem value="new">{t('bannerEvolution.new')}</MenuItem>
                     <MenuItem value="removed">{t('bannerEvolution.removed')}</MenuItem>
                   </Select>
                 </FormControl>
                 
                 <FormControl fullWidth size="small">
                   <InputLabel>{t('bannerEvolution.bannerType')}</InputLabel>
                   <Select
                     multiple
                     value={selectedBannerTypes}
                     onChange={(e) => setSelectedBannerTypes(e.target.value)}
                     renderValue={(selected) => selected.length === 0 ? t('bannerEvolution.allTypes') : `${selected.length} selected`}
                   >
                     {bannerData && [...new Set(bannerData.map(b => b.category).filter(Boolean))].map(bannerType => (
                       <MenuItem key={bannerType} value={bannerType}>{bannerType}</MenuItem>
                     ))}
                   </Select>
                 </FormControl>
                 
                 {/* View Controls */}
                 <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                   <Typography variant="subtitle2" gutterBottom>
                     {t('bannerEvolution.viewOptions')}
                   </Typography>
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={stackedView}
                         onChange={(e) => setStackedView(e.target.checked)}
                         color="primary"
                         size="small"
                       />
                     }
                     label={t('bannerEvolution.stackedView')}
                   />
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={textOverlay}
                         onChange={(e) => setTextOverlay(e.target.checked)}
                         color="primary"
                         size="small"
                       />
                     }
                     label={t('bannerEvolution.textOverlay')}
                   />
                   
                   {/* AI Insights Button — experimental, admin-only */}
                   {showAiInsights && (
                   <Box sx={{ mt: 2 }}>
                     <Button
                       variant="contained"
                       color="secondary"
                       startIcon={aiLoading ? <CircularProgress size={16} /> : <ChartBar size={16} />}
                       disabled={aiLoading || !bannerData || !availableDates.length}
                       onClick={analyzeBannerChanges}
                       sx={{ mr: 1 }}
                     >
                       {aiLoading ? 'Analyzing...' : 'AI Insights'}
                     </Button>
                     <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                       Get AI-powered analysis of banner changes in the selected period
                     </Typography>
                   </Box>
                   )}
                 </Box>
               </Box>
             </Grid>
           </Grid>
         </CardContent>
       </Card>

       {/* Banner Display */}
       <Box id="banner-cards-section">
         <Box>
           <Typography variant="h5" gutterBottom>
             {t('bannerEvolution.activeBanners', { count: lifecycleBanners.length })}
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             {t('bannerEvolution.activeBannersDescription', { startDate, endDate })}
           </Typography>
           {lifecycleBanners.length > 0 && (() => {
             const pageCount = Math.ceil(lifecycleBanners.length / BANNERS_PER_PAGE);
             const safePage = Math.min(bannerPage, pageCount);
             const pageStart = (safePage - 1) * BANNERS_PER_PAGE;
             const pageBanners = lifecycleBanners.slice(pageStart, pageStart + BANNERS_PER_PAGE);
             const pagination = pageCount > 1 ? (
               <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                 <Typography variant="body2" color="text.secondary">
                   {t('bannerEvolution.bannerPageRange', {
                     from: pageStart + 1,
                     to: pageStart + pageBanners.length,
                     total: lifecycleBanners.length,
                   })}
                 </Typography>
                 <Pagination
                   count={pageCount}
                   page={safePage}
                   onChange={(_, value) => {
                     setBannerPage(value);
                     document.getElementById('banner-cards-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                   }}
                   color="primary"
                   shape="rounded"
                 />
               </Box>
             ) : null;
             return (
               <>
                 {pagination}
                 <Card>
                   <CardContent>
                     {renderGroupedBanners(pageBanners, 'normal')}
                   </CardContent>
                 </Card>
                 {pageCount > 1 && (
                   <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                     <Pagination
                       count={pageCount}
                       page={safePage}
                       onChange={(_, value) => {
                         setBannerPage(value);
                         document.getElementById('banner-cards-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                       }}
                       color="primary"
                       shape="rounded"
                     />
                   </Box>
                 )}
               </>
             );
           })()}
           {lifecycleBanners.length === 0 && (
             <Card>
               <CardContent>
                 <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                   <Typography variant="body1" color="text.secondary">
                     {t('bannerEvolution.noActiveBanners')}
                   </Typography>
                 </Box>
               </CardContent>
             </Card>
           )}
         </Box>
       </Box>

      {/* Banner Detail Dialog */}
      <BannerDetailDialog />
      
      {showAiInsights && <AIInsightsDialog />}
      </>
      )}
    </Box>
  );
};

export default BannerEvolutionView;
